import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { timezones } from '../../../utils/timezones';
import { getTimezone, saveTimezone, UserKeys } from '../../../api/users-api';
import { ModuleList } from '../../../shared/analytics/module-list';

import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SharedTabHeader from '../../../shared/shared-tab-header';
export default function SetTimezone() {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();

  const { administrationRoles, changeLogRoles } = useUserSettings();

  const {
    data: currentTimezone,
    isLoading: isTimezoneLoading,
    error: timezoneError,
    isError: isTimezoneError,
  } = useQuery({
    queryKey: [UserKeys.Timezone],
    queryFn: () => getTimezone(),
  });

  const [selectedTimezone, setSelectedTimezone] = useState<string>('');

  useEffect(() => {
    if (!isTimezoneLoading && currentTimezone) {
      setSelectedTimezone(currentTimezone);
    }
  }, [isTimezoneLoading, currentTimezone]);

  useEffect(() => {
    if (isTimezoneError) {
      handleErrors({
        error: timezoneError,
        snackbarMessage: Messages.Errors.Network.Loading('timezone data'),
      });
    }
  }, [isTimezoneError, timezoneError]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => saveTimezone(selectedTimezone!, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Timezone'),
      });
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('timezone'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.update_timezone, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleTimezoneChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSelectedTimezone(event.target.value);
  }

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.UpdateTimezone.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  if (isTimezoneLoading) {
    return (
      <Stack gap={2}>
        <SharedTabHeader title="Set Timezone" />
        <Box sx={{ px: 1 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose your preferred default time zone.
          </Typography>

          <Stack direction="row" gap={1}>
            <Skeleton
              sx={{
                width: 400,
                height: 40,
              }}
              variant="rounded"
            />
          </Stack>
        </Box>
      </Stack>
    );
  }
  const hasUpdatePermission = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.UpdateTimezone.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Set Timezone" />
      <Box sx={{ px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Choose your preferred default time zone.
        </Typography>

        <Tooltip
          arrow
          title={
            !hasUpdatePermission
              ? `You need the "Can update Timezone" permission to make changes`
              : ''
          }
        >
          <Stack direction="row" gap={1}>
            <TextField
              inputProps={{
                id: 'timeZone',
              }}
              InputLabelProps={{
                htmlFor: 'timeZone',
              }}
              disabled={!hasUpdatePermission}
              select
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 450,
                    },
                  },
                },
              }}
              label="Time Zone"
              sx={{ minWidth: 400 }}
              value={selectedTimezone}
              onChange={handleTimezoneChange}
            >
              {timezones.map((timezone) => (
                <MenuItem key={timezone} value={timezone}>
                  {timezone}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              loading={isPending}
              type="submit"
              onClick={handleSubmit}
              disabled={isPending || !hasUpdatePermission}
            >
              Save
            </Button>
          </Stack>
        </Tooltip>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      </Box>
    </Stack>
  );
}
